// The owner's day summary (DESIGN.md §8; docs/plans/phase1-reports.md §2.11, P5, P6, P13).
//
// A short written sentence first, then tables. The sentence is built from fixed templates
// with no adjectives, in parts, so the web app can make each number a link to its detail
// report when the reader may open it.

import type { Money } from '../money.ts';
import { formatMoney } from '../money.ts';

export interface WaitingStats {
  readonly count: number;
  /** Nearest-rank median, whole minutes; null when nobody was seen. */
  readonly medianMinutes: number | null;
  /** Nearest-rank 90th percentile, whole minutes. */
  readonly p90Minutes: number | null;
}

function nearestRank(sorted: readonly number[], fraction: number): number | null {
  if (sorted.length === 0) return null;
  const rank = Math.max(1, Math.ceil(fraction * sorted.length));
  return sorted[rank - 1] ?? null;
}

/**
 * Waiting times from arrival to the first consultation (P13). Negative waits (arrival
 * back-dated after service started) count as zero rather than being dropped.
 */
export function waitingStats(minutes: readonly number[]): WaitingStats {
  const sorted = minutes.map((m) => Math.max(0, Math.floor(m))).sort((a, b) => a - b);
  return {
    count: sorted.length,
    medianMinutes: nearestRank(sorted, 0.5),
    p90Minutes: nearestRank(sorted, 0.9),
  };
}

export const SUMMARY_LINKS = ['opd_register', 'revenue', 'collections', 'claims'] as const;
export type SummaryLink = (typeof SUMMARY_LINKS)[number];

export interface SummaryPart {
  readonly text: string;
  readonly link?: SummaryLink;
}

/** `1,214`: thousands separated with commas, without depending on a locale build. */
export function formatCount(count: number): string {
  if (!Number.isSafeInteger(count)) throw new RangeError('A count must be a whole number');
  return String(count).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

/**
 * "Yesterday 214 patients, KES 486,300 billed, KES 402,100 collected, KES 38,000 payer
 * share not yet claimed." `when` is "Yesterday", "Today" or "On 22 Sep 2026".
 */
export function daySummaryParts(input: {
  readonly when: string;
  readonly patients: number;
  readonly billed: Money;
  readonly collected: Money;
  readonly payerShareUnclaimed: Money;
  readonly unpricedCount: number;
}): SummaryPart[] {
  const parts: SummaryPart[] = [
    { text: `${input.when} ` },
    { text: plural(input.patients, 'patient', 'patients'), link: 'opd_register' },
    { text: ', ' },
    { text: `${formatMoney(input.billed)} billed`, link: 'revenue' },
    { text: ', ' },
    { text: `${formatMoney(input.collected)} collected`, link: 'collections' },
    { text: ', ' },
    {
      text: `${formatMoney(input.payerShareUnclaimed)} payer share not yet claimed`,
      link: 'claims',
    },
    { text: '.' },
  ];
  if (input.unpricedCount > 0)
    parts.push({
      text: ` ${plural(input.unpricedCount, 'charge has', 'charges have')} no price yet.`,
      link: 'revenue',
    });
  return parts;
}

/** The sentence as plain text, for print and for readers without the detail permissions. */
export function summaryText(parts: readonly SummaryPart[]): string {
  return parts.map((part) => part.text).join('');
}
